import React, { useContext, useEffect } from "react";
import { graphql, useStaticQuery } from "gatsby";
import WhatsNewHeroSection from "@src/components/partials/whats-new-2023/WhatsNewHeroSection";
import DojoIslandSection from "@src/components/partials/whats-new-2023/DojoIslandSection";
import { ModalContext, ModalType } from "@src/components/modals/ModalController";
import WhatsNewFeaturesSection from "@src/components/partials/whats-new-2023/WhatsNewFeaturesSection";
import ActivityCornerSection from "@src/components/partials/whats-new-2023/ActivityCornerSection";
import SmallUpdatesSection from "@src/components/partials/whats-new-2023/SmallUpdatesSection";
import SEO from "@src/components/SEO";
import { TranslationContext } from "@src/components/translation/TranslationContext";
import { useLocation } from "@reach/router";
import { logEvent } from "@src/utils/logClient";
import ShareModal from "@src/components/partials/whats-new-2023/ShareModal";

const WhatsNew2023: React.FC = () => {
  // some images are still missing!
  const gqlData = useStaticQuery(graphql`
    {
      hero_background: file(name: { eq: "whats-new-hero-background" }) {
        publicURL
      }
      hero_background_mobile: file(name: { eq: "whats-new-hero-background-mobile" }) {
        publicURL
      }
      hero_image: file(name: { eq: "whats-new-trophy" }) {
        childImageSharp {
          gatsbyImageData
        }
      }
      directus {
        page_whats_new_2023 {
          youtube_video_id
          messaging_image {
            id
            filename_disk
            file {
              childImageSharp {
                gatsbyImageData
              }
            }
          }
          messaging_features
          messaging_cta_href
          dojo_islands_video
          dojo_islands_features
          mojostore_illustration {
            id
            filename_disk
            file {
              childImageSharp {
                gatsbyImageData
              }
            }
          }
          dojostore_features
          dojostore_cta_href
          activity_corner_features
          activity_corner_cta_href
          small_updates_image {
            id
            filename_disk
            file {
              publicURL
            }
          }
          small_updates_features
          small_updates_img_1_cta
        }
      }
    }
  `);

  const {
    hero_background,
    hero_background_mobile,
    hero_image,
    directus: {
      page_whats_new_2023: {
        youtube_video_id,
        dojo_islands_video,
        dojo_islands_features,
        messaging_image,
        messaging_features,
        messaging_cta_href,
        mojostore_illustration,
        dojostore_features,
        dojostore_cta_href,
        activity_corner_features,
        activity_corner_cta_href,
        small_updates_image,
        small_updates_features,
        small_updates_img_1_cta,
      },
    },
  } = gqlData;

  const { showModal } = useContext(ModalContext);
  const { translate: t } = useContext(TranslationContext);

  const whatsNewHeroProps = {
    background: [hero_background_mobile.publicURL, hero_background.publicURL],
    heroImage: hero_image,
    title: "directus.page_whats_new_2023.title",
    subtitle: "directus.page_whats_new_2023.subtitle",
    videoCtaLabel: "directus.page_whats_new_2023.cta_text",
    videoId: youtube_video_id,
    openVideoModal: () => showModal(ModalType.VideoModal, { youtubeID: youtube_video_id }),
  };

  const dojoIslandSectionProps = {
    videoUrl: dojo_islands_video,
    title: "directus.page_whats_new_2023.dojo_islands_title",
    subtitle: "directus.page_whats_new_2023.dojo_islands_subtitle",
    ctaLabel: "directus.page_whats_new_2023.dojo_islands_cta_text",
    ctaUrl: "/dojo-islands",
    features: dojo_islands_features.map((item, index) => ({
      icon: item.icon,
      title: `directus.page_whats_new_2023.dojo_islands_features.heading_${index + 1}`,
      text: `directus.page_whats_new_2023.dojo_islands_features.text_${index + 1}`,
    })),
  };

  const messagingSectionProps = {
    image: messaging_image,
    title: "directus.page_whats_new_2023.messaging_title",
    ctaLabel: "directus.page_whats_new_2023.messaging_cta_text",
    ctaUrl: messaging_cta_href,
    eventName: "web.external_page.whatsnew.messaging.tap",
    features: messaging_features.map((_, index) => ({
      title: `directus.page_whats_new_2023.messaging_features.title_${index + 1}`,
      text: `directus.page_whats_new_2023.messaging_features.text_${index + 1}`,
    })),
  };

  const dojoStoreSectionProps = {
    image: mojostore_illustration,
    title: "directus.page_whats_new_2023.dojostore_heading",
    subtitle: "directus.page_whats_new_2023.dojostore_subtitle",
    ctaLabel: "directus.page_whats_new_2023.dojostore_cta_text",
    ctaUrl: dojostore_cta_href,
    eventName: "web.external_page.whatsnew.dojostore.tap",
    features: dojostore_features.map((_, index) => ({
      title: `directus.page_whats_new_2023.dojostore_features.heading_${index + 1}`,
      text: `directus.page_whats_new_2023.dojostore_features.text_${index + 1}`,
    })),
  };

  const activityCornerSectionProps = {
    title: "directus.page_whats_new_2023.activity_corner_title",
    subtitle: "directus.page_whats_new_2023.activity_corner_subtitle",
    ctaLabel: "directus.page_whats_new_2023.activity_corner_cta_text",
    ctaUrl: activity_corner_cta_href,
    features: activity_corner_features.map((item, index) => ({
      icon: item.icon,
      title: `directus.page_whats_new_2023.activity_corner_features.heading_${index + 1}`,
      text: `directus.page_whats_new_2023.activity_corner_features.text_${index + 1}`,
    })),
  };

  const smallUpdatesEventNames = [
    "web.external_page.whatsnew.small_updates.connect_families.tap",
    "web.external_page.whatsnew.small_updates.whole_team.tap",
    "web.external_page.whatsnew.small_updates.dojo_tutor.tap",
  ];

  const smallUpdatesSectionProps = {
    image: "https://static.classdojo.com/uploads/e784a508-fe48-45eb-85c0-2b1522cea86c.svg",
    title: "directus.page_whats_new_2023.small_updates_title",
    features: small_updates_features.map((item, index) => ({
      icon: item.icon,
      imageUrl: item.image_url,
      title: `directus.page_whats_new_2023.small_updates_features.title_${index + 1}`,
      text: `directus.page_whats_new_2023.small_updates_features.description_${index + 1}`,
      ctaLabel: `directus.page_whats_new_2023.small_updates_features.cta_text_${index + 1}`,
      ctaUrl: item.cta_href,
      eventName: smallUpdatesEventNames[index],
    })),
  };
  const location = useLocation();
  const urlSearchParams = new URLSearchParams(location.search);
  const params = Object.fromEntries(urlSearchParams.entries());
  useEffect(() => {
    logEvent({
      eventName: "web.external_page.whatsnew.page_view",
      eventValue: window.location.href,
      metadata: params,
    });
  }, []);
  return (
    <>
      <SEO
        title="New goodies for a new school year"
        description="Introducing new products and features to make this the best school year ever"
        image="https://static.classdojo.com/uploads/57e176e4-31b1-46a2-a1d7-bce250f82fe9.png"
        twitter={{
          card: "summary_large_image",
          site: "@classdojo",
          creator: "@classdojo",
          image: "https://static.classdojo.com/uploads/57e176e4-31b1-46a2-a1d7-bce250f82fe9.png",
        }}
      />
      <ShareModal />
      <WhatsNewHeroSection {...whatsNewHeroProps} />
      <DojoIslandSection {...dojoIslandSectionProps} />
      <WhatsNewFeaturesSection {...messagingSectionProps} />
      <WhatsNewFeaturesSection {...dojoStoreSectionProps} />
      <ActivityCornerSection {...activityCornerSectionProps} />
      <SmallUpdatesSection {...smallUpdatesSectionProps} />
    </>
  );
};

export default WhatsNew2023;
